import { useMutation } from '@apollo/client';
import type { Editor } from '@tiptap/core';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useRef, useState } from 'react';
import { remark } from 'remark';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Stack,
  useToast,
} from '@eluve/components';
import { EluveEditor, EluveEditorHandle } from '@eluve/eluve-editor';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { openMailTo } from '@eluve/navigator-utils';
import { generateDocumentPdf } from '@eluve/pdf-rendering';
import { useAnalytics } from '@eluve/posthog-react';

import { AppointmentLanguageSelector } from './AppointmentLanguageSelector';
import { updateDocumentReferenceContentMutation } from './documents.operations';

const DEBOUNCE_MS = 500;

export type DocumentTabContentProps = {
  documentId: string;
  tenantId: string;
  title: string;
  content: string;
  readonly?: boolean;
};

export const DocumentTabContent: React.FC<DocumentTabContentProps> = ({
  documentId,
  tenantId,
  title,
  content,
  readonly = false,
}) => {
  const { capture } = useAnalytics();
  const toast = useToast();
  const editorRef = useRef<EluveEditorHandle | null>(null);
  const [copied, setCopied] = useState(false);
  const [editorContent, setEditorContent] = useState(content);
  const debouncedContent = useDebounce(editorContent, DEBOUNCE_MS);
  const hasPendingEdit = useRef(false);

  const [updateContent] = useMutation(updateDocumentReferenceContentMutation);

  const handleEditorUpdate = (editor?: Editor) => {
    if (readonly || !editor) return;
    const markdown = getTiptapMarkdownContentFromEditor(editor);
    if (markdown !== editorContent) {
      hasPendingEdit.current = true;
      setEditorContent(markdown);
    }
  };

  useEffect(() => {
    if (!hasPendingEdit.current) return;
    if (debouncedContent === content) return;
    hasPendingEdit.current = false;

    updateContent({
      variables: { tenantId, id: documentId, content: debouncedContent },
    })
      .then(() => {
        capture('document_content_edited', {
          document_id: documentId,
        });
      })
      .catch(() => {
        toast.error('Failed to save changes');
      });
  }, [debouncedContent, tenantId, documentId, content, updateContent, toast]);

  const handleCopy = async () => {
    try {
      const markdown = editorRef.current?.getMarkdown() ?? content;
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      toast.success('Copied to clipboard');
      capture('document_copied', { document_id: documentId });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleExportPdf = async () => {
    try {
      const markdown = editorRef.current?.getMarkdown() ?? content;
      const blob = await generateDocumentPdf(title, markdown);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      capture('document_exported_pdf', { document_id: documentId });
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const handleEmail = async () => {
    const markdown = editorRef.current?.getMarkdown() ?? content;
    const processed = String(
      await remark().data('settings', { setext: true }).process(markdown),
    );
    // Strip markdown escape characters for clean plain-text email body
    const plainBody = processed.replace(/\\([[\](){}*_~`#>+\-.|!])/g, '$1');
    await openMailTo({
      to: undefined,
      subject: title,
      body: plainBody,
      onClipboardFallback: () => {
        toast.info('Document copied to clipboard — paste into your email');
      },
    });
    capture('document_emailed', { document_id: documentId });
  };

  return (
    <Stack className="gap-[26px]" fullHeight align="stretch">
      <Stack justify="between" gap={2} direction="horizontal" wrap>
        <Stack gap={4} direction="horizontal">
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Icon name={copied ? 'Check' : 'Copy'} size="auto" />
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Icon name="FileInput" size="auto" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={handleExportPdf}>
                <Icon name="FileText" size="lg" />
                Download PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleEmail}>
                <Icon name="Mail" size="lg" />
                Email Document
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Stack>
        <AppointmentLanguageSelector inline outputLanguage />
      </Stack>

      <div className="flex-1 overflow-y-auto rounded-lg border border-border-primary px-6 py-5">
        <EluveEditor
          ref={editorRef}
          content={content}
          disabled={readonly}
          onUpdate={handleEditorUpdate}
        />
      </div>
    </Stack>
  );
};
